import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw01 } from '@untitledui/icons';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import {
  getActiveImportKinds,
  type ImportJob,
  useImportStatus,
} from '@/hooks/integrations/useImportStatus';
import { useRecentImportItems } from '@/hooks/integrations/useRecentImportItems';
import type { BadgeColors } from '@/lib/base/badges/badge-types';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { ProgressBar } from '@/lib/base/progress-indicators/progress-indicators';
import { apiPost } from '@/lib/http/client';
import { ActivityFeed } from '@/pages/dashboard/components/ActivityFeed';

interface UnifiedImportStatusProps {
  orgId: string;
  /**
   * The connected account this card belongs to. Everything below is scoped to
   * it — progress, ETA, activity feed, and the Sync Calendar affordance — so a
   * card only ever reports what its own account is doing. Rendering nothing is
   * the correct outcome for an account with no jobs of its own.
   */
  socialAccountId: number;
  gcalEnabled?: boolean;
}

type DisplayStatus = 'active' | 'active-backoff' | 'completed' | 'failed';

const statusDisplayMap: Record<
  DisplayStatus,
  { text: string; color: BadgeColors }
> = {
  active: { text: 'Importing…', color: 'blue' },
  'active-backoff': { text: 'Rate Limited', color: 'warning' },
  completed: { text: 'Completed', color: 'success' },
  failed: { text: 'Failed', color: 'error' },
};

// When the remaining time is longer than this threshold, hide the actual ETA
// and show a friendly "we'll email you" message instead. The backend mirrors
// this threshold (LONG_IMPORT_THRESHOLD in apps/vendor_imports/notifications.py)
// to decide whether to actually send a completion email.
const LONG_IMPORT_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const LONG_IMPORT_MESSAGE = "We'll email you when it's done";

/**
 * Find the job with the latest estimated completion time (furthest from done).
 * If no ETAs, find the one with the lowest percent complete.
 * Returns null if no active jobs.
 */
function findMaxEtaJob(jobs: ImportJob[]): ImportJob | null {
  const activeJobs = jobs.filter(j => j.status === 'active');

  if (activeJobs.length === 0) {
    // No active jobs - check if any completed or failed
    if (jobs.length === 0) {
      return null;
    }
    // Return the most recently completed/failed
    return jobs.reduce((latest, job) => {
      const latestTime = latest.completed_at || latest.created_at;
      const jobTime = job.completed_at || job.created_at;
      return jobTime > latestTime ? job : latest;
    });
  }

  // Find job with max ETA (will complete last)
  let maxEtaJob: ImportJob | null = null;
  let maxEta: string | null = null;

  for (const job of activeJobs) {
    const eta = job.progress.estimated_completion_at;
    if (eta) {
      if (!maxEta || eta > maxEta) {
        maxEta = eta;
        maxEtaJob = job;
      }
    }
  }

  // If we found a job with ETA, return it
  if (maxEtaJob) {
    return maxEtaJob;
  }

  // No ETAs - find job with lowest percent complete (furthest from done)
  return activeJobs.reduce((lowest, job) => {
    return job.progress.percent_complete < lowest.progress.percent_complete
      ? job
      : lowest;
  });
}

export default function UnifiedImportStatus({
  orgId,
  socialAccountId,
  gcalEnabled = true,
}: UnifiedImportStatusProps) {
  const [, setTick] = useState(0); // Force re-render for ETA updates
  const [syncing, setSyncing] = useState(false);
  const queryClient = useQueryClient();

  // Use the consolidated hook for fetching import jobs
  const importStatus = useImportStatus({
    orgId,
    socialAccountId,
  });
  const activeImportKinds = useMemo(
    () => getActiveImportKinds(importStatus.services),
    [importStatus.services]
  );
  const { data: recentItems } = useRecentImportItems({
    orgId,
    enabled: importStatus.hasActiveImports,
    activeKinds: activeImportKinds,
    socialAccountId,
  });

  const { jobs } = importStatus;
  const loading = importStatus.isLoading;
  const { error } = importStatus;
  // The "Sync Calendar" button below kicks off a Google-Calendar-specific
  // import (`/gcal-sync/import-jobs/start/`), so gate it on gcal absence
  // specifically — a testcalendar/other-calendar import in flight should
  // not hide the gcal sync action.
  const hasGcalJobs = importStatus.services.some(
    svc => svc.providerName === 'gcal' && svc.hasJobs
  );

  const handleSyncCalendar = async () => {
    if (syncing) {
      return;
    }
    setSyncing(true);
    try {
      await apiPost(`/api/orgs/${orgId}/gcal-sync/import-jobs/start/`, {
        social_account_id: socialAccountId,
      });
      // Refetch import status to show the newly created job
      await queryClient.invalidateQueries({
        queryKey: ['import-status', orgId],
      });
    } catch {
      // 409 means import already in progress — that's fine
    } finally {
      setSyncing(false);
    }
  };

  // Find the job to display
  const displayJob = useMemo(() => findMaxEtaJob(jobs), [jobs]);

  // ETA countdown ticker - only depends on stable values to avoid restarting interval
  const shouldTick =
    displayJob?.status === 'active' &&
    !!(
      displayJob?.progress.estimated_completion_at || displayJob?.is_backing_off
    );

  useEffect(() => {
    if (!shouldTick) {
      return;
    }

    const intervalId = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(intervalId);
  }, [shouldTick]);

  // Loading state
  if (loading) {
    return (
      <div
        className='py-4'
        role='progressbar'
        aria-label='Loading import status'
      >
        <div className='w-full h-2 bg-quaternary rounded-md animate-pulse' />
      </div>
    );
  }

  // Error state (only show if no jobs to display)
  if (error && jobs.length === 0) {
    return (
      <div className='py-2 px-3 bg-utility-error-50 rounded-lg border border-utility-error-200'>
        <p className='text-sm text-utility-error-700'>{error}</p>
      </div>
    );
  }

  // No jobs and no sync button to show
  if (!displayJob && !(gcalEnabled && !hasGcalJobs)) {
    return null;
  }

  // Determine display status (only when we have a job to show)
  let displayStatus: DisplayStatus = 'completed';
  let statusDisplay = statusDisplayMap[displayStatus];
  let progressValue = 0;
  let etaText: string | null = null;

  if (displayJob) {
    if (displayJob.status === 'active' && displayJob.is_backing_off) {
      displayStatus = 'active-backoff';
    } else {
      displayStatus = displayJob.status;
    }

    statusDisplay = statusDisplayMap[displayStatus];
    const isComplete = displayJob.status === 'completed';
    progressValue = isComplete ? 100 : displayJob.progress.percent_complete;

    if (displayJob.status === 'active') {
      if (displayJob.is_backing_off && displayJob.backoff_until) {
        const backoffUntil = new Date(displayJob.backoff_until);
        const now = new Date();
        const secondsRemaining = Math.max(
          0,
          Math.ceil((backoffUntil.getTime() - now.getTime()) / 1000)
        );
        if (secondsRemaining > 60) {
          const minutes = Math.ceil(secondsRemaining / 60);
          etaText = `Resuming in ~${minutes}m`;
        } else if (secondsRemaining > 0) {
          etaText = `Resuming in ${secondsRemaining}s`;
        } else {
          etaText = 'Resuming…';
        }
      } else if (displayJob.progress.estimated_completion_at) {
        const eta = new Date(displayJob.progress.estimated_completion_at);
        const msRemaining = eta.getTime() - Date.now();
        if (msRemaining > LONG_IMPORT_THRESHOLD_MS) {
          etaText = LONG_IMPORT_MESSAGE;
        } else {
          etaText = `${formatDistanceToNow(eta)} remaining`;
        }
      } else if (displayJob.progress.items_processed === 0) {
        etaText = 'Starting import…';
      }
    }
  }

  return (
    <div className='space-y-2'>
      {/* Import progress bar */}
      {displayJob && (
        <div className='flex items-center gap-2 py-1'>
          <div className='flex-1'>
            <ProgressBar value={progressValue} min={0} max={100} />
          </div>
          <div className='shrink-0 flex justify-end'>
            {etaText ? (
              <span className='text-xs text-tertiary'>{etaText}</span>
            ) : (
              <BadgeWithDot type='color' size='sm' color={statusDisplay.color}>
                {statusDisplay.text}
              </BadgeWithDot>
            )}
          </div>
        </div>
      )}

      {/* Activity feed — shown when items exist and import is active */}
      {recentItems.length > 0 && displayJob?.status === 'active' && (
        <ActivityFeed items={recentItems} />
      )}

      {/* Sync Calendar button — shown when gcal is enabled but no gcal import exists */}
      {gcalEnabled && !hasGcalJobs && (
        <div className='flex items-center py-1'>
          <Button
            color='secondary'
            size='sm'
            onClick={handleSyncCalendar}
            isDisabled={syncing}
            iconLeading={RefreshCw01}
          >
            {syncing ? 'Syncing…' : 'Sync calendar'}
          </Button>
        </div>
      )}
    </div>
  );
}
